import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Bell, Trash2, Loader2 } from "lucide-react";
import { DateTime } from "luxon";
import { toast } from "sonner";
import {
  cancelReminder,
  updateCheckpointReminder,
  createCheckpointReminder,
  CheckpointReminder,
} from "../../api/checkpointReminders";
import useRetryFetch from "../../hooks/useRetryFetch";

interface SetReminderModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  prescriptionId: string;
  activityTitle: string;
  existingReminder?: CheckpointReminder | null;
  onReminderUpdated?: () => void;
}

export const SetReminderModal: React.FC<SetReminderModalProps> = ({
  open,
  onOpenChange,
  prescriptionId,
  activityTitle,
  existingReminder,
  onReminderUpdated,
}) => {
  const { t, i18n } = useTranslation();
  const retryFetch = useRetryFetch();

  const [reminderDate, setReminderDate] = useState("");
  const [reminderTime, setReminderTime] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);

  const isEditing = !!existingReminder;

  // Initialize date and time
  useEffect(() => {
    if (open) {
      if (existingReminder) {
        // Editing existing reminder
        const scheduledDateTime = DateTime.fromISO(existingReminder.scheduledDateTime);
        setReminderDate(scheduledDateTime.toFormat("yyyy-MM-dd"));
        setReminderTime(scheduledDateTime.toFormat("HH:mm"));
      } else {
        // Creating new reminder - default to tomorrow at 9 AM
        const tomorrow = DateTime.now().plus({ days: 1 }).set({ hour: 9, minute: 0 });
        setReminderDate(tomorrow.toFormat("yyyy-MM-dd"));
        setReminderTime(tomorrow.toFormat("HH:mm"));
      }
    }
  }, [open, existingReminder]);

  const handleCancel = async () => {
    if (!existingReminder) return;

    setIsCancelling(true);
    try {
      await cancelReminder(retryFetch, existingReminder._id);
      toast.success(t("reminder_cancelled_success"));
      onOpenChange(false);
      if (onReminderUpdated) {
        onReminderUpdated();
      }
    } catch (error) {
      console.error("Failed to cancel reminder:", error);
      toast.error(t("reminder_cancel_failed"));
    } finally {
      setIsCancelling(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!reminderDate || !reminderTime) {
      toast.error(t("please_select_date_time"));
      return;
    }

    const dateTime = DateTime.fromFormat(
      `${reminderDate} ${reminderTime}`,
      "yyyy-MM-dd HH:mm"
    );

    if (dateTime < DateTime.now()) {
      toast.error(t("reminder_must_be_future"));
      return;
    }

    setIsSubmitting(true);
    try {
      if (isEditing) {
        // Update existing reminder
        await updateCheckpointReminder(retryFetch, existingReminder._id, {
          scheduledDateTime: dateTime.toISO() || "",
        });
        toast.success(t("reminder_updated_success"));
      } else {
        // Create new reminder
        await createCheckpointReminder(retryFetch, {
          prescriptionId: prescriptionId,
          scheduledDateTime: dateTime.toISO() || "",
          activityTitle: activityTitle,
          reminderAction: "start_new_engagement",
        });
        toast.success(t("reminder_created_success"));
      }

      onOpenChange(false);
      if (onReminderUpdated) {
        onReminderUpdated();
      }
    } catch (error) {
      console.error("Failed to save reminder:", error);
      toast.error(isEditing ? t("reminder_update_failed") : t("reminder_create_failed"));
    } finally {
      setIsSubmitting(false);
    }
  };

  // For editing mode, show current reminder info
  let scheduledTime, isPast;
  if (existingReminder) {
    scheduledTime = DateTime.fromISO(existingReminder.scheduledDateTime).setLocale(i18n.language);
    isPast = scheduledTime < DateTime.now();
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bell className="w-5 h-5 text-primary" />
            {isEditing ? t("manage_reminder") : t("set_reminder")}
          </DialogTitle>
          <DialogDescription>
            {isEditing ? t("manage_reminder_description") : t("set_reminder_description")}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            {/* Activity Title */}
            <div>
              <Label className="text-sm font-medium text-muted-foreground">
                {t("activity")}
              </Label>
              <p className="mt-1 text-sm font-semibold">
                {activityTitle || t("untitled_activity")}
              </p>
            </div>

            {/* Current Scheduled Time (only for editing) */}
            {isEditing && existingReminder && scheduledTime && (
              <div className={`p-3 rounded-lg border ${
                isPast
                  ? 'bg-orange-50 border-orange-200 dark:bg-orange-900/20 dark:border-orange-800'
                  : 'bg-primary/5 border-primary/20 dark:bg-primary/20 dark:border-primary/80'
              }`}>
                <Label className="text-xs font-medium text-muted-foreground">
                  {t("current_reminder")}
                </Label>
                <p className={`mt-1 text-sm font-semibold ${
                  isPast
                    ? 'text-orange-700 dark:text-orange-400'
                    : 'text-primary dark:text-primary/70'
                }`}>
                  {scheduledTime.toLocaleString(DateTime.DATETIME_MED)} ({scheduledTime.toRelative()})
                </p>
              </div>
            )}

            {/* Date & Time */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">
                {isEditing ? t("new_schedule") : t("schedule_reminder")}
              </Label>
              <div className="grid grid-cols-2 gap-3">
                <Input
                  id="reminder-date"
                  type="date"
                  value={reminderDate}
                  onChange={(e) => setReminderDate(e.target.value)}
                  required
                />
                <Input
                  id="reminder-time"
                  type="time"
                  value={reminderTime}
                  onChange={(e) => setReminderTime(e.target.value)}
                  required
                />
              </div>
            </div>
          </div>

          <DialogFooter className="flex gap-2 sm:gap-0">
            {isEditing && (
              <Button
                type="button"
                variant="destructive"
                onClick={handleCancel}
                disabled={isCancelling || isSubmitting}
                className="gap-1.5"
              >
                {isCancelling ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4" />
                )}
                {isCancelling ? t("cancelling") : t("cancel_reminder")}
              </Button>
            )}
            <Button
              type="submit"
              disabled={isSubmitting || isCancelling}
              className="gap-1.5"
            >
              {isSubmitting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Bell className="w-4 h-4" />
              )}
              {isSubmitting
                ? (isEditing ? t("updating") : t("creating"))
                : (isEditing ? t("update_reminder") : t("set_reminder"))
              }
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
